<script setup lang="ts">
import { mdiChevronRight, mdiClose } from "@mdi/js";
import { useQuery } from "@tanstack/vue-query";
import { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";
import { storeToRefs } from "pinia";
import { useSettings } from "~/store/settings";
import { useBetslip } from "~/store/betslip";
import NavIcon from "../Nav/NavIcon.vue";
import { mediaLibrariesPath } from "~/utils/constants";

interface AZSport extends ISport {
	displayName: string;
	sportLink: string;
	sportIcon: string;
}

defineProps<{ sortedSports: { [key: string]: AZSport[] } }>();
const betSlip = useBetslip();

const { personalizedMenu } = storeToRefs(betSlip);

const settings = useSettings();
const { azMenu } = storeToRefs(settings);
const colorMode = useColorMode();

const handleClick = (sport: AZSport) => {
	if (!sport.shouldDisplayCarousel && !personalizedMenu.value.includes(sport.sportId)) {
		personalizedMenu.value.push(sport.sportId);
	}
	azMenu.value = false;
};
</script>

<template>
	<div class="lg:[column-count:3] gap-4 lg:h-auto h-[calc(100%-40px)] overflow-y-auto">
		<div v-for="(group, idx) in sortedSports" :key="idx" class="lg:[break-inside:avoid]">
			<div class="font-bold mb-2">
				{{ idx }}
			</div>
			<template v-for="sport in group" :key="sport.sportId">
				<NuxtLink
					class="w-full bg-light-50 dark:bg-dark-600 rounded-lg flex flex-nowrap justify-between p-2 mb-2 base-text"
					:href="sport.sportLink" @click="handleClick(sport)">
					<div class="flex text-xs font-bold items-center">
						<DataFreeGuard>
							<NavIcon :src="`/icons/${sport.sportId.replace('esports-', '').replace('---', '-')}.png`"
								:alt="sport.displayName" class="mr-4 w-6 h-6" :provider="null" :fallback="colorMode.value === 'light'
										? `${useRuntimeConfig().public.kenticoDomain}/${mediaLibrariesPath}/fallback-dark.png`
										: `${useRuntimeConfig().public.kenticoDomain}/${mediaLibrariesPath}/fallback-light.png`
									" />
						</DataFreeGuard>
						{{ sport.displayName.includes("sports.") ? sport.name : sport.displayName }}
					</div>
					<UiIcon :path="mdiChevronRight" class="w-5 h-5 dark:fill-light-50" />
				</NuxtLink>
			</template>
		</div>
	</div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
